/**
 * Loans data access.
 *
 * Every figure on these screens is computed by the API from the ledger; the
 * client formats it and nothing else. The one exception is the schedule
 * PREVIEW in the form, which uses the same shared engine the API saves with,
 * so what the form shows is what the loan will be.
 *
 * Loan mutations invalidate only loan queries. Expense, sales and dashboard
 * caches are untouched on purpose: a loan never changes those figures, and an
 * invalidation that implied it could would be the first sign of the two
 * becoming one number.
 */

import type { ScopeFilter } from '../../lib/locationContext';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  LendingPolicy,
  LendingPolicyInput,
  LoanSettlementInput,
  MemberRole,
  SettlementQuote,
  LoanBorrowerMatch,
  LoanCategory,
  LoanCloseInput,
  LoanDeductionAgreementInput,
  LoanDeductionInput,
  LoanDeductionSourceView,
  LoanDirection,
  LoanInput,
  LoanList,
  LoanReport,
  LoanRepaymentInput,
  LoanReversalInput,
  LoanTopUpInput,
  LoanAdjustmentInput,
  LoanSort,
  LoanStatus,
  LoanSummary,
  LoanUpdate,
  LoanView,
} from '@cenox/shared';
import { api, qs } from '../../lib/api';
import { useBusinessPath } from '../../lib/auth';

export interface LoanListFilters {
  direction: LoanDirection;
  category?: LoanCategory | undefined;
  status?: LoanStatus | undefined;
  q?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  branchId?: string | undefined;
  scopeCountry?: string | undefined;
  scopeState?: string | undefined;
  sort: LoanSort;
  dir: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

const KEY = 'loans';

export function useLoans(filters: LoanListFilters, enabled = true): UseQueryResult<LoanList> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: [KEY, 'list', path(''), filters],
    queryFn: () => api.get<LoanList>(path(`/loans${qs({ ...filters })}`)),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function useLoanSummary(
  filters: { direction: LoanDirection; category?: LoanCategory | undefined } & ScopeFilter,
  enabled = true,
): UseQueryResult<LoanSummary> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: [KEY, 'summary', path(''), filters],
    queryFn: async () => (await api.get<{ data: LoanSummary }>(path(`/loans/summary${qs(filters)}`))).data,
    enabled,
  });
}

export function useLoanReport(filters: {
  direction: LoanDirection;
  from?: string | undefined;
  to?: string | undefined;
  category?: LoanCategory | undefined;
  branchId?: string | undefined;
  scopeCountry?: string | undefined;
  scopeState?: string | undefined;
}, enabled = true): UseQueryResult<LoanReport> {
  const path = useBusinessPath();
  return useQuery({
    enabled,
    queryKey: [KEY, 'report', path(''), filters],
    queryFn: async () => (await api.get<{ data: LoanReport }>(path(`/loans/report${qs(filters)}`))).data,
  });
}

export function useLoan(id: string | undefined): UseQueryResult<LoanView> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: [KEY, 'detail', path(''), id],
    queryFn: async () => (await api.get<{ data: LoanView }>(path(`/loans/${id}`))).data,
    enabled: Boolean(id),
  });
}

export function useLoanPurposes(): UseQueryResult<Array<{ id: string | null; name: string }>> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: [KEY, 'purposes', path('')],
    queryFn: async () =>
      (await api.get<{ data: Array<{ id: string | null; name: string }> }>(path('/loans/purposes'))).data,
    staleTime: 5 * 60_000,
  });
}

export function useBorrowerSearch(
  kind: 'rider' | 'staff' | null,
  q: string,
): UseQueryResult<LoanBorrowerMatch[]> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: [KEY, 'borrowers', path(''), kind, q],
    queryFn: async () =>
      (await api.get<{ data: LoanBorrowerMatch[] }>(path(`/loans/borrowers${qs({ kind, q })}`))).data,
    enabled: kind !== null,
    staleTime: 30_000,
  });
}

export function useDeductionSources(id: string, enabled: boolean): UseQueryResult<LoanDeductionSourceView[]> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: [KEY, 'deduction-sources', path(''), id],
    queryFn: async () =>
      (await api.get<{ data: LoanDeductionSourceView[] }>(path(`/loans/${id}/deduction-sources`))).data,
    enabled,
  });
}

/** Every loan figure is derived from the ledger, so any change refreshes them all. */
function useLoanMutation<I>(
  send: (path: (p: string) => string, input: I) => Promise<{ data: LoanView }>,
): UseMutationResult<LoanView, Error, I> {
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: I) => (await send(path, input)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
      // A loan payment moves the drawer and the accounts too.
      void queryClient.invalidateQueries({ queryKey: ['accountability'] });
      void queryClient.invalidateQueries({ queryKey: ['money'] });
    },
  });
}

export const useCreateLoan = () =>
  useLoanMutation<LoanInput>((path, input) => api.post(path('/loans'), input));

export const useUpdateLoan = (id: string) =>
  useLoanMutation<LoanUpdate>((path, input) => api.patch(path(`/loans/${id}`), input));

export const useIssueLoan = (id: string) =>
  useLoanMutation<void>((path) => api.post(path(`/loans/${id}/issue`), {}));

export const useRecordRepayment = (id: string) =>
  useLoanMutation<LoanRepaymentInput>((path, input) => api.post(path(`/loans/${id}/repayments`), input));

/** Lend more on a loan already out. */
export const useTopUpLoan = (id: string) =>
  useLoanMutation<LoanTopUpInput>((path, input) => api.post(path(`/loans/${id}/top-ups`), input));

/** Correct a balance down without money or a write-off. */
export const useAdjustLoan = (id: string) =>
  useLoanMutation<LoanAdjustmentInput>((path, input) => api.post(path(`/loans/${id}/adjustments`), input));

export const useReverseTransaction = (id: string) =>
  useLoanMutation<LoanReversalInput & { transactionId: string }>((path, { transactionId, ...input }) =>
    api.post(path(`/loans/${id}/transactions/${transactionId}/reverse`), input),
  );

export const useCancelLoan = (id: string) =>
  useLoanMutation<LoanCloseInput>((path, input) => api.post(path(`/loans/${id}/cancel`), input));

export const useWriteOffLoan = (id: string) =>
  useLoanMutation<LoanCloseInput>((path, input) => api.post(path(`/loans/${id}/write-off`), input));

export const useCreateAgreement = (id: string) =>
  useLoanMutation<LoanDeductionAgreementInput>((path, input) =>
    api.post(path(`/loans/${id}/deduction-agreement`), input),
  );

export const useRevokeAgreement = (id: string) =>
  useLoanMutation<{ reason: string }>((path, input) =>
    api.post(path(`/loans/${id}/deduction-agreement/revoke`), input),
  );

export const useRecordDeduction = (id: string) =>
  useLoanMutation<LoanDeductionInput>((path, input) => api.post(path(`/loans/${id}/deductions`), input));

/** Uploaded before the loan is saved, and linked by id. */
export function useUploadLoanDocument(): UseMutationResult<{ id: string; filename: string }, Error, File> {
  const path = useBusinessPath();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.upload<{ id: string; filename: string }>(path('/loans/documents'), form);
    },
  });
}

/** A key for one submission of one form, reused if that submission is retried. */
export function newSubmissionKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `k-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ── Early settlement and fees ──────────────────────────────────────────────

export function useSettlementQuote(id: string, on: string, enabled: boolean): UseQueryResult<SettlementQuote> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: [KEY, 'settlement', path(''), id, on],
    queryFn: async () => (await api.get<{ data: SettlementQuote }>(path(`/loans/${id}/settlement${qs({ on })}`))).data,
    enabled,
  });
}

export const useSettleLoan = (id: string) =>
  useLoanMutation<LoanSettlementInput>((path, input) => api.post(path(`/loans/${id}/settle`), input));

export const useApplyLateFee = (id: string) =>
  useLoanMutation<{ instalmentId: string; idempotencyKey: string }>((path, input) =>
    api.post(path(`/loans/${id}/late-fees`), input),
  );

export const useWaiveCharge = (id: string) =>
  useLoanMutation<{ chargeId: string; reason: string }>((path, { chargeId, reason }) =>
    api.post(path(`/loans/${id}/charges/${chargeId}/waive`), { reason }),
  );

export const useRemoveLoanDocument = (id: string) =>
  useLoanMutation<{ reason: string }>((path, input) => api.del(path(`/loans/${id}/document`), input));

// ── Lending policy and access ──────────────────────────────────────────────

export function useLendingPolicies(): UseQueryResult<{ policies: LendingPolicy[]; countries: string[] }> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: [KEY, 'policies', path('')],
    queryFn: async () =>
      (await api.get<{ data: { policies: LendingPolicy[]; countries: string[] } }>(path('/loans/policies'))).data,
  });
}

export function useSaveLendingPolicy(): UseMutationResult<LendingPolicy, Error, LendingPolicyInput> {
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: LendingPolicyInput) =>
      (await api.put<{ data: LendingPolicy }>(path('/loans/policies'), input)).data,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: [KEY, 'policies'] }),
  });
}

export interface LoanAccessRow {
  memberId: string;
  name: string;
  email: string | null;
  role: MemberRole;
  permissions: string[];
  editable: boolean;
}

export function useLoanAccess(enabled: boolean): UseQueryResult<LoanAccessRow[]> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: [KEY, 'access', path('')],
    queryFn: async () => (await api.get<{ data: LoanAccessRow[] }>(path('/loans/access'))).data,
    enabled,
  });
}

export function useSetLoanAccess(): UseMutationResult<LoanAccessRow, Error, { memberId: string; permissions: string[] }> {
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ memberId, permissions }) =>
      (await api.put<{ data: LoanAccessRow }>(path(`/loans/access/${memberId}`), { permissions })).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY, 'access'] });
      void queryClient.invalidateQueries({ queryKey: ['members'] });
    },
  });
}
